import React, { useState, useMemo, useEffect } from 'react';
import {
  ArrowRightLeft,
  Search,
  Filter,
  ShieldCheck,
  Zap,
  Globe,
  TrendingUp,
  Clock,
  ExternalLink,
  Info,
  CheckCircle2,
  AlertTriangle,
  Bookmark,
  Sparkles,
  ArrowRight,
  Database,
  Building2,
  Layers,
  ChevronDown,
  RefreshCw,
  Eye,
  Activity,
  Award,
  Sliders,
  DollarSign,
  HelpCircle,
  FileText,
  BookmarkCheck,
} from 'lucide-react';
import {
  TransferProvider,
  TransferCorridor,
  CanonicalTransferQuote,
  ComparisonMode,
  TransferSortOption,
  PaymentMethodCode,
  PayoutMethodCode,
  TransferWatchlistItem,
  DeliverableP5,
  AdrRecord,
} from '../../types';
import { transferEngine, PAYMENT_METHOD_LABELS, PAYOUT_METHOD_LABELS } from '../../services/transferEngine';
import { financialFormat, formatRateDisplay } from '../../services/fxEngine';
import { TransferProviderCard } from './TransferProviderCard';
import { TransferDetailDrawer } from './TransferDetailDrawer';
import { DELIVERABLES_P5, ADR_TRANSFER_RECORDS } from '../../data/transferProvidersData';
import { TRANSFER_CORRIDORS_LIST } from '../../data/transferCorridorsData';

interface MoneyTransferHubProps {
  onNavigateHome: () => void;
  onNavigateFx: () => void;
  onNavigateGovernance: () => void;
}

type TabType = 'comparator' | 'corridors' | 'providers' | 'watchlist' | 'methodology' | 'command-center' | 'architecture-p5';

export const MoneyTransferHub: React.FC<MoneyTransferHubProps> = ({
  onNavigateHome,
  onNavigateFx,
  onNavigateGovernance,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('comparator');

  // Comparison State
  const [mode, setMode] = useState<ComparisonMode>('SEND_AMOUNT');
  const [sendingCountry, setSendingCountry] = useState<string>('US');
  const [receivingCountry, setReceivingCountry] = useState<string>('IN');
  const [sendCurrency, setSendCurrency] = useState<string>('USD');
  const [receiveCurrency, setReceiveCurrency] = useState<string>('INR');
  const [amount, setAmount] = useState<number>(1000);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodCode | 'ANY'>('ANY');
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethodCode | 'ANY'>('ANY');
  const [sortBy, setSortBy] = useState<TransferSortOption>('BEST_RECIPIENT_AMOUNT');
  const [includePromos, setIncludePromos] = useState<boolean>(true);

  // Drawer
  const [selectedQuoteForDrawer, setSelectedQuoteForDrawer] = useState<CanonicalTransferQuote | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  // Watchlist & Telemetry
  const [watchlist, setWatchlist] = useState<TransferWatchlistItem[]>(() => transferEngine.getWatchlist());
  const [watchAddedNotice, setWatchAddedNotice] = useState<boolean>(false);
  const [selectedCorridorRegion, setSelectedCorridorRegion] = useState<string>('ALL');

  // Country Options
  const countryOptions = [
    { code: 'US', name: 'United States', currency: 'USD', flag: '🇺🇸' },
    { code: 'CA', name: 'Canada', currency: 'CAD', flag: '🇨🇦' },
    { code: 'GB', name: 'United Kingdom', currency: 'GBP', flag: '🇬🇧' },
    { code: 'FR', name: 'France (Eurozone)', currency: 'EUR', flag: '🇫🇷' },
    { code: 'DE', name: 'Germany (Eurozone)', currency: 'EUR', flag: '🇩🇪' },
    { code: 'AU', name: 'Australia', currency: 'AUD', flag: '🇦🇺' },
    { code: 'SG', name: 'Singapore', currency: 'SGD', flag: '🇸🇬' },
    { code: 'AE', name: 'United Arab Emirates', currency: 'AED', flag: '🇦🇪' },
    { code: 'IN', name: 'India', currency: 'INR', flag: '🇮🇳' },
    { code: 'MX', name: 'Mexico', currency: 'MXN', flag: '🇲🇽' },
    { code: 'PH', name: 'Philippines', currency: 'PHP', flag: '🇵🇭' },
    { code: 'NG', name: 'Nigeria', currency: 'NGN', flag: '🇳🇬' },
    { code: 'GH', name: 'Ghana', currency: 'GHS', flag: '🇬🇭' },
    { code: 'KE', name: 'Kenya', currency: 'KES', flag: '🇰🇪' },
    { code: 'CM', name: 'Cameroon (CEMAC)', currency: 'XAF', flag: '🇨🇲' },
    { code: 'SN', name: 'Senegal (UEMOA)', currency: 'XOF', flag: '🇸🇳' },
    { code: 'MA', name: 'Morocco', currency: 'MAD', flag: '🇲🇦' },
    { code: 'PK', name: 'Pakistan', currency: 'PKR', flag: '🇵🇰' },
    { code: 'CO', name: 'Colombia', currency: 'COP', flag: '🇨🇴' },
  ];

  // Auto-sync currency when country changes
  const handleSelectSendingCountry = (cCode: string) => {
    setSendingCountry(cCode);
    const country = countryOptions.find((c) => c.code === cCode);
    if (country) setSendCurrency(country.currency);
  };

  const handleSelectReceivingCountry = (cCode: string) => {
    setReceivingCountry(cCode);
    const country = countryOptions.find((c) => c.code === cCode);
    if (country) setReceiveCurrency(country.currency);
  };

  // Swap sending & receiving
  const handleSwap = () => {
    const oldSend = sendingCountry;
    const oldRecv = receivingCountry;
    handleSelectSendingCountry(oldRecv);
    handleSelectReceivingCountry(oldSend);
  };

  // Generate Real-time Quotes
  const comparisonResults = useMemo(() => {
    return transferEngine.generateQuotes({
      sendingCountry,
      receivingCountry,
      sendCurrency,
      receiveCurrency,
      amount: isNaN(amount) || amount <= 0 ? 1000 : amount,
      mode,
      paymentMethod,
      payoutMethod,
      sortBy,
      includePromotions: includePromos,
      newCustomerOnly: false,
    });
  }, [
    sendingCountry,
    receivingCountry,
    sendCurrency,
    receiveCurrency,
    amount,
    mode,
    paymentMethod,
    payoutMethod,
    sortBy,
    includePromos,
  ]);

  // Add current search to watchlist
  const handleAddToWatchlist = () => {
    const item = transferEngine.addToWatchlist({
      sendingCountry,
      receivingCountry,
      sendCurrency,
      receiveCurrency,
      typicalAmount: amount,
      targetRecipientAmount: comparisonResults.bestRecipientAmount,
      currentBestProvider: comparisonResults.bestProviderName,
      currentBestRecipientAmount: comparisonResults.bestRecipientAmount,
      lastChecked: new Date().toISOString(),
    });
    setWatchlist(transferEngine.getWatchlist());
    setWatchAddedNotice(true);
    setTimeout(() => setWatchAddedNotice(false), 3000);
  };

  const handleRemoveFromWatchlist = (id: string) => {
    transferEngine.removeFromWatchlist(id);
    setWatchlist(transferEngine.getWatchlist());
  };

  const openQuoteDetails = (quote: CanonicalTransferQuote) => {
    setSelectedQuoteForDrawer(quote);
    setIsDrawerOpen(true);
  };

  const telemetry = transferEngine.getTelemetry();

  return (
    <div className="w-full min-h-screen bg-[#FBFBFC] text-[#1A1A1A] pb-24">
      {/* Top Banner & Title Bar */}
      <div className="border-b border-[#E5E7EB] bg-white sticky top-0 z-20 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#0E7044] animate-pulse"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                  YamooPay Money Transfer Intelligence Engine
                </span>
                <span className="text-[10px] font-semibold bg-[#F3F4F6] text-[#4B5563] px-2 py-0.5 rounded-full">
                  PROMPT 05
                </span>
              </div>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-[#1A1A1A]">
                Global Remittance & Provider Intelligence
              </h1>
              <p className="text-xs text-[#6B7280]">
                Independent fee verification, FX markup detection, and live recipient payout comparisons.
              </p>
            </div>

            {/* Benchmark Pill & Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={onNavigateFx}
                className="px-3 py-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <TrendingUp className="w-3.5 h-3.5 text-[#0E7044]" />
                <span>FX Benchmark Engine</span>
              </button>

              <button
                onClick={onNavigateGovernance}
                className="px-3 py-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Database className="w-3.5 h-3.5 text-[#1E40AF]" />
                <span>Trust Layer</span>
              </button>
            </div>
          </div>

          {/* Navigation Sub-Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar pt-1 border-t border-[#F3F4F6]">
            {[
              { id: 'comparator', label: 'Live Comparator', icon: <ArrowRightLeft className="w-4 h-4" /> },
              { id: 'corridors', label: 'Global Corridors', icon: <Globe className="w-4 h-4" /> },
              { id: 'providers', label: 'Provider Directory', icon: <Building2 className="w-4 h-4" /> },
              { id: 'watchlist', label: `My Watchlist (${watchlist.length})`, icon: <Bookmark className="w-4 h-4" /> },
              { id: 'methodology', label: 'Methodology & Disclosures', icon: <ShieldCheck className="w-4 h-4" /> },
              { id: 'command-center', label: 'Command Center', icon: <Activity className="w-4 h-4" /> },
              { id: 'architecture-p5', label: 'Architecture & ADRs', icon: <FileText className="w-4 h-4" /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`py-3 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-[#0E7044] text-[#0E7044] bg-[#F0FDF4]/50'
                    : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A] hover:border-[#D1D5DB]'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* ========================================================================= */}
        {/* TAB 1: LIVE TRANSFER COMPARATOR */}
        {/* ========================================================================= */}
        {activeTab === 'comparator' && (
          <div className="space-y-6">
            {/* Search Parameter Card */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-6">
              {/* Mode Toggle & Presets */}
              <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-[#F3F4F6]">
                <div className="flex items-center gap-2 p-1 bg-[#F3F4F6] rounded-xl">
                  <button
                    onClick={() => setMode('SEND_AMOUNT')}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      mode === 'SEND_AMOUNT'
                        ? 'bg-white text-[#1A1A1A] shadow-xs'
                        : 'text-[#6B7280] hover:text-[#1A1A1A]'
                    }`}
                  >
                    Mode A: I want to send
                  </button>
                  <button
                    onClick={() => setMode('RECEIVE_TARGET')}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      mode === 'RECEIVE_TARGET'
                        ? 'bg-white text-[#1A1A1A] shadow-xs'
                        : 'text-[#6B7280] hover:text-[#1A1A1A]'
                    }`}
                  >
                    Mode B: Recipient should receive
                  </button>
                </div>

                <div className="flex items-center gap-2 text-xs font-medium text-[#6B7280]">
                  <span>Amount Presets:</span>
                  {[300, 500, 1000, 2500, 5000].map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setAmount(preset)}
                      className={`px-2.5 py-1 rounded-lg border text-xs font-bold cursor-pointer transition-colors ${
                        amount === preset
                          ? 'border-[#0E7044] bg-[#F0FDF4] text-[#0E7044]'
                          : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-[#F9FAFB]'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Inputs: From Country, To Country, Amount */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                {/* Sending Country */}
                <div className="md:col-span-4 space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
                    Send From (Origin)
                  </label>
                  <select
                    value={sendingCountry}
                    onChange={(e) => handleSelectSendingCountry(e.target.value)}
                    className="w-full p-3 bg-white border border-[#E5E7EB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-hidden"
                  >
                    {countryOptions.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.name} ({c.currency})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Swap Button */}
                <div className="md:col-span-1 flex items-center justify-center pt-5">
                  <button
                    onClick={handleSwap}
                    title="Swap Countries"
                    className="p-2.5 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] hover:bg-[#E5E7EB] text-[#4B5563] transition-colors cursor-pointer"
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                  </button>
                </div>

                {/* Receiving Country */}
                <div className="md:col-span-4 space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
                    Receive In (Destination)
                  </label>
                  <select
                    value={receivingCountry}
                    onChange={(e) => handleSelectReceivingCountry(e.target.value)}
                    className="w-full p-3 bg-white border border-[#E5E7EB] rounded-xl text-sm font-bold text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-hidden"
                  >
                    {countryOptions.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.name} ({c.currency})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Amount Input */}
                <div className="md:col-span-3 space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
                    {mode === 'SEND_AMOUNT' ? `Send Amount (${sendCurrency})` : `Target Receive (${receiveCurrency})`}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={10}
                      step={50}
                      value={amount}
                      onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                      className="w-full p-3 pr-14 bg-white border border-[#E5E7EB] rounded-xl text-sm font-black text-[#1A1A1A] focus:ring-2 focus:ring-[#0E7044] focus:outline-hidden"
                    />
                    <span className="absolute right-3.5 top-3.5 text-xs font-bold text-[#6B7280]">
                      {mode === 'SEND_AMOUNT' ? sendCurrency : receiveCurrency}
                    </span>
                  </div>
                </div>
              </div>

              {/* Filters Row: Pay-in, Payout, Sort, Promotions */}
              <div className="pt-4 border-t border-[#F3F4F6] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Pay-in filter */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-[#6B7280]">Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as any)}
                    className="w-full p-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-xs font-semibold text-[#1A1A1A]"
                  >
                    <option value="ANY">Any Payment Method</option>
                    <option value="BANK_TRANSFER">Bank Account (Standard)</option>
                    <option value="ACH">Direct ACH / Wire</option>
                    <option value="DEBIT_CARD">Debit Card (Instant)</option>
                    <option value="OPEN_BANKING">Open Banking / SEPA</option>
                    <option value="CREDIT_CARD">Credit Card</option>
                  </select>
                </div>

                {/* Payout filter */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-[#6B7280]">Payout Method</label>
                  <select
                    value={payoutMethod}
                    onChange={(e) => setPayoutMethod(e.target.value as any)}
                    className="w-full p-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-xs font-semibold text-[#1A1A1A]"
                  >
                    <option value="ANY">Any Payout Method</option>
                    <option value="BANK_DEPOSIT">Direct Bank Deposit</option>
                    <option value="CASH_PICKUP">Cash Pickup</option>
                    <option value="MOBILE_MONEY">Mobile Money (MTN, Orange, M-Pesa)</option>
                    <option value="MOBILE_WALLET">Mobile Wallet (GCash, Maya)</option>
                  </select>
                </div>

                {/* Sort By */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-[#6B7280]">Rank Results By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as TransferSortOption)}
                    className="w-full p-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#0E7044]"
                  >
                    <option value="BEST_RECIPIENT_AMOUNT">Recipient Gets Most (Default)</option>
                    <option value="LOWEST_TOTAL_COST">Lowest Total Cost</option>
                    <option value="FASTEST">Fastest Delivery Speed</option>
                    <option value="LOWEST_FEE">Lowest Transfer Fee</option>
                    <option value="BEST_EXCHANGE_RATE">Best Exchange Rate</option>
                    <option value="USER_VALUE_SCORE">YamooPay Value Score</option>
                  </select>
                </div>

                {/* Promo check & Watchlist button */}
                <div className="flex items-end justify-between gap-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-[#4B5563] cursor-pointer pb-2">
                    <input
                      type="checkbox"
                      checked={includePromos}
                      onChange={(e) => setIncludePromos(e.target.checked)}
                      className="rounded text-[#0E7044] focus:ring-[#0E7044]"
                    />
                    <span>Include New User Promos</span>
                  </label>

                  <button
                    onClick={handleAddToWatchlist}
                    className="py-2 px-3 bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
                  >
                    <Bookmark className="w-3.5 h-3.5 text-[#0E7044]" />
                    <span>Watchlist</span>
                  </button>
                </div>
              </div>

              {watchAddedNotice && (
                <div className="p-3 bg-[#DCFCE7] border border-[#86EFAC] rounded-xl text-xs font-bold text-[#0E7044] flex items-center gap-2 animate-in fade-in">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Corridor added to your Transfer Watchlist!</span>
                </div>
              )}
            </div>

            {/* Corridor Benchmark Banner */}
            <div className="bg-[#F0FDF4] border border-[#DCFCE7] rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#0E7044] text-white flex items-center justify-center font-bold text-sm">
                  FX
                </div>
                <div>
                  <div className="text-xs font-bold text-[#0E7044] flex items-center gap-1.5">
                    <span>YamooPay Benchmark Market Rate</span>
                    <span className="text-[10px] bg-white px-2 py-0.2 rounded border border-[#DCFCE7] text-[#0E7044]">
                      Zero Markup Reference
                    </span>
                  </div>
                  <div className="text-sm font-mono font-extrabold text-[#1A1A1A]">
                    1 {sendCurrency} = {formatRateDisplay(comparisonResults.benchmarkRate)} {receiveCurrency}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-[#4B5563]">
                <div>
                  Comparing <strong>{comparisonResults.quotes.length} verified providers</strong>
                </div>
                <span>•</span>
                <div>
                  Synced: <strong>{comparisonResults.benchmarkTimestamp.substring(11, 16)} UTC</strong>
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#1A1A1A]">
                Comparison Results for {sendingCountry} → {receivingCountry} ({financialFormat(amount, sendCurrency)} {sendCurrency})
              </h3>
              <span className="text-xs font-medium text-[#6B7280]">
                Sorted by: <strong className="text-[#0E7044]">{sortBy.replace(/_/g, ' ')}</strong>
              </span>
            </div>

            {/* Results Grid */}
            <div className="space-y-3">
              {comparisonResults.quotes.map((quote, idx) => (
                <TransferProviderCard
                  key={quote.id}
                  quote={quote}
                  rankIndex={idx}
                  onOpenDetails={openQuoteDetails}
                />
              ))}

              {comparisonResults.quotes.length === 0 && (
                <div className="p-12 text-center bg-white border border-[#E5E7EB] rounded-2xl space-y-3">
                  <AlertTriangle className="w-10 h-10 text-[#D97706] mx-auto" />
                  <h4 className="text-base font-bold text-[#1A1A1A]">No providers currently match this combination</h4>
                  <p className="text-xs text-[#6B7280] max-w-md mx-auto">
                    Try selecting "Any Payment Method" or "Any Payout Method" above to view all verified transfer routes.
                  </p>
                </div>
              )}
            </div>

            {/* Independent Disclaimer Banner */}
            <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-2xl flex items-start gap-3 text-xs text-[#6B7280]">
              <Info className="w-4 h-4 text-[#0E7044] shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-bold text-[#1A1A1A]">YamooPay Consumer Protection Guarantee</p>
                <p>
                  We rank providers purely based on recipient amount, transparency, and verified data. Commercial affiliate relationships do not influence the organic rankings. Always verify terms on the provider’s website before initiating payment.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: GLOBAL CORRIDOR CATALOG */}
        {/* ========================================================================= */}
        {activeTab === 'corridors' && (
          <div className="space-y-6">
            {/* Header & Filter by Expansion Wave / Region */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-black text-[#1A1A1A]">Global Money Transfer Corridors</h3>
                <p className="text-xs text-[#6B7280]">
                  Track international remittance routes across Waves 1 to 5 with live payout rails and regulatory profiles.
                </p>
              </div>

              <div className="flex items-center gap-1.5 p-1 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold">
                {['ALL', 'NORTH_AMERICA', 'EUROPE', 'ASIA_PACIFIC', 'MIDDLE_EAST', 'AFRICA'].map((reg) => (
                  <button
                    key={reg}
                    onClick={() => setSelectedCorridorRegion(reg)}
                    className={`px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
                      selectedCorridorRegion === reg
                        ? 'bg-[#0E7044] text-white'
                        : 'text-[#6B7280] hover:text-[#1A1A1A]'
                    }`}
                  >
                    {reg.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Corridors Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {TRANSFER_CORRIDORS_LIST.filter(
                (c) => selectedCorridorRegion === 'ALL' || c.regionGroup === selectedCorridorRegion
              ).map((corridor) => (
                <div
                  key={corridor.id}
                  className="p-5 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#0E7044] transition-all space-y-4 shadow-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase bg-[#F3F4F6] text-[#4B5563] px-2 py-0.5 rounded-md">
                      Wave {corridor.wave} • {corridor.regionGroup.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs font-bold text-[#0E7044] bg-[#DCFCE7] px-2 py-0.5 rounded-full">
                      {corridor.availableProvidersCount} Providers
                    </span>
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-[#1A1A1A]">{corridor.corridorName}</h4>
                    <p className="text-xs text-[#6B7280] line-clamp-2 mt-1">
                      {corridor.corridorDescription}
                    </p>
                  </div>

                  <div className="p-3 bg-[#F9FAFB] rounded-xl space-y-1 text-xs">
                    <div className="flex justify-between text-[#6B7280]">
                      <span>Pair:</span>
                      <span className="font-mono font-bold text-[#1A1A1A]">{corridor.sendCurrency} → {corridor.receiveCurrency}</span>
                    </div>
                    <div className="flex justify-between text-[#6B7280]">
                      <span>Average Delivery:</span>
                      <span className="font-semibold text-[#1A1A1A]">{corridor.averageSpeed}</span>
                    </div>
                    <div className="flex justify-between text-[#6B7280]">
                      <span>Search Demand Score:</span>
                      <span className="font-bold text-[#0E7044]">{corridor.searchDemandScore}/100</span>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      handleSelectSendingCountry(corridor.sendingCountry);
                      handleSelectReceivingCountry(corridor.receivingCountry);
                      setActiveTab('comparator');
                    }}
                    className="w-full py-2.5 bg-[#F3F4F6] hover:bg-[#0E7044] hover:text-white rounded-xl text-xs font-bold text-[#1A1A1A] flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <span>Compare this Corridor</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: PROVIDER DIRECTORY & FACTUAL REVIEWS */}
        {/* ========================================================================= */}
        {activeTab === 'providers' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-black text-[#1A1A1A]">Transfer Provider Directory</h3>
              <p className="text-xs text-[#6B7280]">
                Audited regulatory profiles, integration statuses, and factual pricing methodologies.
              </p>
            </div>

            <div className="space-y-4">
              {transferEngine.getProviders().map((prov) => (
                <div key={prov.id} className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#F3F4F6]">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center font-black text-base">
                        {prov.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-base font-bold text-[#1A1A1A]">{prov.name}</h4>
                          <span className="text-[10px] font-bold bg-[#DCFCE7] text-[#0E7044] px-2 py-0.5 rounded-full">
                            {prov.status}
                          </span>
                        </div>
                        <p className="text-xs text-[#6B7280]">
                          {prov.legalName} • Founded {prov.yearFounded} ({prov.headquarters})
                        </p>
                      </div>
                    </div>

                    <a
                      href={prov.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] rounded-xl text-xs font-bold text-[#4B5563] flex items-center gap-1.5"
                    >
                      <span>Official Website</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div className="space-y-2">
                      <div className="font-bold text-[#1A1A1A]">Editorial Strengths (Pros):</div>
                      <ul className="space-y-1 text-[#4B5563]">
                        {prov.editorialHighlights.pros.map((pro, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#0E7044] shrink-0 mt-0.5" />
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <div className="font-bold text-[#1A1A1A]">Limitations (Cons):</div>
                      <ul className="space-y-1 text-[#4B5563]">
                        {prov.editorialHighlights.cons.map((con, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="text-[#DC2626] font-bold">•</span>
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 bg-[#F9FAFB] rounded-xl text-xs space-y-1">
                    <div className="font-bold text-[#1A1A1A]">Pricing Summary:</div>
                    <p className="text-[#4B5563]">{prov.editorialHighlights.pricingSummary}</p>
                    <div className="pt-2 text-[11px] text-[#6B7280]">
                      <strong>Regulators:</strong> {prov.regulators.join(' • ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: WATCHLIST & SAVED COMPARISONS */}
        {/* ========================================================================= */}
        {activeTab === 'watchlist' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-[#1A1A1A]">My Transfer Watchlist</h3>
                <p className="text-xs text-[#6B7280]">
                  Track target recipient amounts and rate movements on your recurring transfer routes.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {watchlist.map((item) => (
                <div key={item.id} className="p-5 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#1A1A1A] flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-[#0E7044]" />
                      <span>{item.sendingCountry} → {item.receivingCountry}</span>
                    </span>
                    <button
                      onClick={() => handleRemoveFromWatchlist(item.id)}
                      className="text-xs font-bold text-[#DC2626] hover:underline cursor-pointer"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="space-y-1">
                    <div className="text-[11px] text-[#6B7280] uppercase tracking-wider font-semibold">
                      Typical Send Amount
                    </div>
                    <div className="text-lg font-black text-[#1A1A1A]">
                      {financialFormat(item.typicalAmount, item.sendCurrency)} {item.sendCurrency}
                    </div>
                  </div>

                  <div className="p-3 bg-[#F0FDF4] border border-[#DCFCE7] rounded-xl space-y-1 text-xs">
                    <div className="text-[#0E7044] font-semibold">Current Best Payout:</div>
                    <div className="text-base font-black text-[#1A1A1A]">
                      {financialFormat(item.currentBestRecipientAmount, item.receiveCurrency)} {item.receiveCurrency}
                    </div>
                    <div className="text-[11px] text-[#6B7280]">
                      Leading Provider: <strong>{item.currentBestProvider}</strong>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      handleSelectSendingCountry(item.sendingCountry);
                      handleSelectReceivingCountry(item.receivingCountry);
                      setAmount(item.typicalAmount);
                      setActiveTab('comparator');
                    }}
                    className="w-full py-2 bg-[#0E7044] hover:bg-[#0B5936] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    Compare Now
                  </button>
                </div>
              ))}

              {watchlist.length === 0 && (
                <div className="col-span-3 p-12 text-center bg-white border border-[#E5E7EB] rounded-2xl space-y-3">
                  <Bookmark className="w-10 h-10 text-[#9CA3AF] mx-auto" />
                  <h4 className="text-base font-bold text-[#1A1A1A]">No corridors in your watchlist yet</h4>
                  <p className="text-xs text-[#6B7280]">
                    Click the "Watchlist" button in the Live Comparator to monitor your favorite transfer routes.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: METHODOLOGY & DISCLOSURES */}
        {/* ========================================================================= */}
        {activeTab === 'methodology' && (
          <div className="max-w-4xl mx-auto space-y-8 bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-xs text-sm">
            <div className="space-y-2 border-b border-[#F3F4F6] pb-6">
              <div className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                YamooPay Trust & Transparency Standard
              </div>
              <h2 className="text-2xl font-black text-[#1A1A1A]">
                How YamooPay Compares Money Transfers
              </h2>
              <p className="text-[#6B7280]">
                Our independent methodology guarantees that consumers receive the most accurate, transparent, and uncompromised financial intelligence possible.
              </p>
            </div>

            {/* Principle 1: Recipient Amount First */}
            <div className="space-y-3">
              <h3 className="text-base font-bold text-[#1A1A1A] flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[#0E7044] text-white flex items-center justify-center text-xs font-black">
                  1
                </span>
                <span>The Core Principle: Recipient Amount First</span>
              </h3>
              <p className="text-[#4B5563] leading-relaxed">
                The single most vital metric in cross-border money transfers is not the superficial upfront transfer fee. Two competing providers may both advertise "$0 Transfer Fee", yet apply drastically different exchange rates. YamooPay measures the net <strong>Recipient Amount</strong> after all explicit fees and exchange-rate markups are factored in.
              </p>
            </div>

            {/* Principle 2: FX Markup Calculation */}
            <div className="space-y-3">
              <h3 className="text-base font-bold text-[#1A1A1A] flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[#0E7044] text-white flex items-center justify-center text-xs font-black">
                  2
                </span>
                <span>Measuring Hidden FX Markups</span>
              </h3>
              <p className="text-[#4B5563] leading-relaxed">
                We compare every provider’s effective exchange rate against the independent <strong>YamooPay Benchmark FX Rate</strong> (derived from institutional central bank and wholesale market feeds in Prompt 04). The formula applied is:
              </p>
              <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl font-mono text-xs text-[#1A1A1A]">
                FX Markup % = ((YamooPay Benchmark Rate - Provider Rate) / YamooPay Benchmark Rate) × 100
              </div>
            </div>

            {/* Principle 3: Total Transfer Cost */}
            <div className="space-y-3">
              <h3 className="text-base font-bold text-[#1A1A1A] flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[#0E7044] text-white flex items-center justify-center text-xs font-black">
                  3
                </span>
                <span>Estimated Total Cost</span>
              </h3>
              <p className="text-[#4B5563] leading-relaxed">
                The total cost of a transfer includes both visible and hidden elements:
              </p>
              <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl font-mono text-xs text-[#1A1A1A]">
                Estimated Total Cost = Explicit Transfer Fee + (Amount Converted × FX Markup %) + Known Payment Surcharges
              </div>
            </div>

            {/* Principle 4: Strict Anti-Pay-to-Win Pledge */}
            <div className="p-6 bg-[#FEF3C7] border border-[#FDE68A] rounded-2xl space-y-3">
              <div className="text-sm font-bold text-[#92400E] flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                <span>Strict Anti-Pay-to-Win Pledge</span>
              </div>
              <p className="text-xs text-[#92400E] leading-relaxed">
                Under no circumstances does a commercial affiliate partnership alter the organic rank of a provider on YamooPay. <strong>The provider that delivers the highest recipient amount or best value to the consumer ranks #1.</strong> Commercial relationships and affiliate commissions are strictly segregated from organic ranking algorithms.
              </p>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 6: TRANSFER COMMAND CENTER */}
        {/* ========================================================================= */}
        {activeTab === 'command-center' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-black text-[#1A1A1A]">Transfer Command Center</h3>
              <p className="text-xs text-[#6B7280]">
                Real-time operational telemetry, provider health monitors, and affiliate integrity tracking.
              </p>
            </div>

            {/* Telemetry KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-1">
                <div className="text-[11px] font-bold uppercase text-[#6B7280]">Active Providers</div>
                <div className="text-2xl font-black text-[#0E7044]">{telemetry.totalActiveProviders}</div>
                <div className="text-[10px] text-[#6B7280]">{telemetry.degradedProvidersCount} degraded</div>
              </div>

              <div className="p-4 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-1">
                <div className="text-[11px] font-bold uppercase text-[#6B7280]">Quote Success Rate</div>
                <div className="text-2xl font-black text-[#1A1A1A]">{telemetry.quoteSuccessRate}%</div>
                <div className="text-[10px] text-[#0E7044]">Avg latency: {telemetry.avgLatencyMs}ms</div>
              </div>

              <div className="p-4 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-1">
                <div className="text-[11px] font-bold uppercase text-[#6B7280]">Active Corridors</div>
                <div className="text-2xl font-black text-[#1A1A1A]">{telemetry.activeCorridorsCount}</div>
                <div className="text-[10px] text-[#6B7280]">Global Waves 1–5</div>
              </div>

              <div className="p-4 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-1">
                <div className="text-[11px] font-bold uppercase text-[#6B7280]">Integrity Score</div>
                <div className="text-2xl font-black text-[#0E7044]">{telemetry.organicVsCommercialIntegrityScore}%</div>
                <div className="text-[10px] text-[#0E7044]">Zero Pay-to-win drift</div>
              </div>
            </div>

            {/* Provider Integration Health Table */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-4">
              <h4 className="text-sm font-bold text-[#1A1A1A]">Provider Health & Integration Feeds</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-[#F9FAFB] border-y border-[#E5E7EB] text-[#6B7280] uppercase font-bold">
                    <tr>
                      <th className="p-3">Provider</th>
                      <th className="p-3">Integration Mode</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Affiliate Program</th>
                      <th className="p-3">Last Checked</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {transferEngine.getProviders().map((p) => (
                      <tr key={p.id} className="hover:bg-[#F9FAFB]">
                        <td className="p-3 font-bold text-[#1A1A1A]">{p.name}</td>
                        <td className="p-3 font-mono text-[#4B5563]">{p.integrationMode}</td>
                        <td className="p-3">
                          <span className="bg-[#DCFCE7] text-[#0E7044] px-2 py-0.5 rounded-full font-bold">
                            {p.status}
                          </span>
                        </td>
                        <td className="p-3 text-[#4B5563]">
                          {p.affiliateProgramAvailable ? 'Approved Partner' : 'Standard Direct Feed'}
                        </td>
                        <td className="p-3 text-[#6B7280]">{p.lastVerified.substring(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 7: ARCHITECTURE & ADRs (P5) */}
        {/* ========================================================================= */}
        {activeTab === 'architecture-p5' && (
          <div className="space-y-8">
            <div className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#0E7044]">
                PROMPT 05 Technical Architecture & ADRs
              </div>
              <h3 className="text-xl font-black text-[#1A1A1A]">
                Money Transfer Engine Master Deliverables & Architectural Records
              </h3>
            </div>

            {/* Architecture Decision Records (ADR-TRANSFER-001 -> 006) */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#6B7280]">
                Architecture Decision Records (ADR-TRANSFER-001 to 006)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ADR_TRANSFER_RECORDS.map((adr) => (
                  <div key={adr.id} className="p-5 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-bold text-[#0E7044] bg-[#DCFCE7] px-2.5 py-0.5 rounded-md">
                        {adr.id}
                      </span>
                      <span className="text-[10px] font-bold bg-[#F3F4F6] text-[#4B5563] px-2 py-0.5 rounded-full">
                        {adr.status}
                      </span>
                    </div>
                    <h5 className="text-sm font-bold text-[#1A1A1A]">{adr.title}</h5>
                    <p className="text-xs text-[#4B5563] leading-relaxed">
                      <strong>Context:</strong> {adr.context}
                    </p>
                    <div className="p-3 bg-[#F9FAFB] rounded-xl text-xs space-y-1">
                      <strong className="text-[#1A1A1A]">Decision:</strong>
                      <p className="text-[#4B5563]">{adr.decision}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Master Deliverables (A -> AU) */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#6B7280]">
                Master Deliverables Specifications (A through AU)
              </h4>
              <div className="space-y-3">
                {DELIVERABLES_P5.map((del) => (
                  <div key={del.code} className="p-5 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-md bg-[#0E7044] text-white flex items-center justify-center font-bold text-xs">
                          {del.code}
                        </span>
                        <h5 className="font-bold text-sm text-[#1A1A1A]">{del.titleEn}</h5>
                      </div>
                      <span className="text-[10px] font-semibold text-[#6B7280]">{del.category}</span>
                    </div>
                    <p className="text-xs text-[#4B5563]">{del.summary}</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pt-2 text-xs text-[#6B7280]">
                      {del.details.map((d, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <span className="text-[#0E7044] font-bold">✓</span>
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Itemized Price & Math Drawer */}
      <TransferDetailDrawer
        quote={selectedQuoteForDrawer}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
};
